import { useRef, useState } from 'react';
import { Accordion, Button, Icon } from '@iwoca/orion';
import { t, Trans } from '@lingui/macro';
import { useAmplitude } from 'hooks/useAmplitude';
import { TOfferDataRequirement } from 'lendingApi/lapiOverrides.types';
import { useGetStateQuery } from 'lendingApi/lendingApiHooks';
import CopyToClipboard from 'react-copy-to-clipboard';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import {
  generateBodyTextString,
  generateCopyStringBody,
  renderInstruction,
  requirementsTitle,
} from '../../helpers/ShareRequirements.helper';
import shareRequirementsStyles from '../../ShareRequirements/ShareRequirements.module.css';
import styles from './ShareRequirements.module.css';

type TProps = {
  requiredDocs: TOfferDataRequirement['actions'];
  optionalDocs?: TOfferDataRequirement['actions'];
};

export const ShareRequirements = ({ requiredDocs, optionalDocs }: TProps) => {
  const { state } = useGetStateQuery();
  const [isCopyClicked, setIsCopyClicked] = useState(false);
  const requirementListRef = useRef<HTMLDivElement>(null);
  const { trackEvent } = useAmplitude();

  const renderedDocumentRequirements = () => {
    const requiredDocsText = requiredDocs.map((requirement, index) => {
      return (
        <li key={index}>
          <span className={styles.title}>
            {requirementsTitle(requirement.type, requirement.quantifier?.value)}
          </span>
          <span className={styles.instruction}>
            {renderInstruction(requirement.type)}
          </span>
        </li>
      );
    });

    const optionalDocsText = optionalDocs?.map((requirement, index) => {
      return (
        <li key={index}>
          <span className={styles.title}>
            {requirementsTitle(requirement.type, requirement.quantifier?.value)}{' '}
            (optional)
          </span>
          <span className={styles.instruction}>
            {renderInstruction(requirement.type)}
          </span>
        </li>
      );
    });

    return (
      <>
        {requiredDocsText && <ul>{requiredDocsText}</ul>}
        {optionalDocsText && <ul>{optionalDocsText}</ul>}
      </>
    );
  };

  const clipboardText = () => {
    let textToCopy: string = '';
    textToCopy = generateCopyStringBody({
      requiredDocs,
      optionalDocs,
    });

    textToCopy = textToCopy + `\n${t`Files must be PDF and no more than 2GB`}`;

    return textToCopy;
  };

  const generateMailToLink = () => {
    let mailtoBody = `${generateBodyTextString({
      docRequirements: requiredDocs,
      newLineBreak: '%0D%0A',
    })}`;

    if (optionalDocs && optionalDocs.length > 0) {
      mailtoBody = `${mailtoBody}${generateBodyTextString({
        docRequirements: optionalDocs,
        newLineBreak: '%0D%0A',
      })}`;
    }

    const firstName = state?.application?.people?.[0]?.first_name;
    const firstNameSpaced = firstName ? ` ${firstName}` : '';
    return `mailto:?body=${t`Hi`}${firstNameSpaced},%0D%0A%0D%0A${t`As discussed, I’m applying for a loan with iwoca on behalf of your business. To verify your trading history I need to provide several documents.`} %0D%0A%0D%0A${t`Please can you send me:`} %0D%0A%0D%0A${mailtoBody} %0D%0A%0D%0A${t`Please ensure files clearly show the business name, are PDF and no more than 2GB.`} %0D%0A%0D%0A${t`Including optional documents may increase your chance of approval.`} &subject=${t`Help with documents for new loan`}`;
  };

  return (
    <Accordion
      frame={false}
      className={shareRequirementsStyles.accordionContainer}
      title={t`Do you need to ask your accounting department or someone else for the documents?`}
    >
      <p className={shareRequirementsStyles.subTitle}>
        <Trans>
          If you don’t have the right things to hand, you can email (or copy and
          paste) this list to someone who does.
        </Trans>
      </p>

      <div
        className={shareRequirementsStyles.requirementsList}
        ref={requirementListRef}
      >
        {renderedDocumentRequirements()}
        <ul>
          <p className={shareRequirementsStyles.description}>
            <Trans>Please make sure that...</Trans>
            <li>
              <Trans>You send the data in PDF format</Trans>
            </li>
            <li>
              <Trans>No file is larger than 50 MB</Trans>
            </li>
            <li>
              <Trans>
                The files are not password protected (or share the password with
                us)
              </Trans>
            </li>
          </p>
        </ul>
      </div>
      <div className={shareRequirementsStyles.copyButton}>
        <CopyToClipboard
          text={clipboardText()}
          onCopy={() => setIsCopyClicked(true)}
        >
          <Button
            variant="tertiary"
            size="small"
            onClick={() =>
              trackEvent(AMPLITUDE_EVENTS.copyRequirementsListClicked)
            }
          >
            <Trans>Copy this list as text</Trans>
            <Icon icon="copyOutline" />
          </Button>
        </CopyToClipboard>
        {isCopyClicked && (
          <div className={shareRequirementsStyles.copyButtonSuccess}>
            <Icon
              icon="checkCircleFilled"
              className={shareRequirementsStyles.copyCheckIcon}
            />
            <p className={shareRequirementsStyles.copyText}>
              <Trans>List copied</Trans>
            </p>
          </div>
        )}
      </div>
      <div>
        <Button
          variant="tertiary"
          size="small"
          href={generateMailToLink()}
          onClick={() =>
            trackEvent(AMPLITUDE_EVENTS.emailRequirementsListClicked)
          }
          className={shareRequirementsStyles.emailButton}
          target="_blank"
          rel="noreferrer"
        >
          <Trans>Email this list to someone</Trans>
          <Icon icon="emailOutline" />
        </Button>
      </div>
    </Accordion>
  );
};
